import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { attendanceSheetStatusEnumInFull } from '@/modules/business/attendance-sheet/enums/attendance-sheet-status.enum';
import { formatarMesAnoLegivel } from '@/shared/utils/datas';
import type { PersonFormDto } from '@/modules/people/presentation/person-form.utils';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import {
  aplicarDesligamentoNoFormulario,
  dataHojeIso,
  listarFolhasNaoConcluidas,
  type FolhaPendenteInfo,
} from '../forms/profiles/desligar-estagiario.utils';

interface DesligarEstagiarioModalProps {
  show: boolean;
  handleHide: () => void;
  getValues: UseFormGetValues<PersonFormDto>;
  setValue: UseFormSetValue<PersonFormDto>;
}

export default function DesligarEstagiarioModal({
  show,
  handleHide,
  getValues,
  setValue,
}: DesligarEstagiarioModalProps) {
  const [dataDesligamento, setDataDesligamento] = useState(dataHojeIso());
  const [folhasPendentes, setFolhasPendentes] = useState<FolhaPendenteInfo[]>([]);
  const [carregandoFolhas, setCarregandoFolhas] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  useEffect(() => {
    if (!show) return;

    setDataDesligamento(dataHojeIso());
    setConfirmando(false);
    setCarregandoFolhas(true);
    setFolhasPendentes([]);

    const estagios = getValues('estagiario.estagios');
    listarFolhasNaoConcluidas(estagios)
      .then(setFolhasPendentes)
      .catch(() => {
        toast.warn('Não foi possível verificar as folhas de frequência.');
        setFolhasPendentes([]);
      })
      .finally(() => setCarregandoFolhas(false));
  }, [show, getValues]);

  const handleConfirmar = () => {
    if (!dataDesligamento) {
      toast.error('Informe a data de desligamento.');
      return;
    }

    setConfirmando(true);
    try {
      const { estagiosAtualizados } = aplicarDesligamentoNoFormulario(
        getValues,
        setValue,
        dataDesligamento,
      );
      toast.success(
        estagiosAtualizados > 0
          ? `Desligamento aplicado em ${estagiosAtualizados} estágio(s). Salve o cadastro para confirmar.`
          : 'Perfil inativado e acesso desabilitado. Salve o cadastro para confirmar.',
      );
      handleHide();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    } finally {
      setConfirmando(false);
    }
  };

  return (
    <Modal show={show} onHide={handleHide} centered>
      {/* Sem <form>: o modal fica dentro do form da página de edição. */}
      <Modal.Header closeButton className="py-2">
        <Modal.Title className="fs-6">Desligar Estagiário</Modal.Title>
      </Modal.Header>
      <Modal.Body className="py-2">
        <p className="small mb-3">
          Definir data de desligamento, inativar perfil de estagiário e desabilitar acesso ao sistema?
        </p>

        <Form.Group className="mb-3">
          <Form.Label className="mb-1 small">Data de desligamento *</Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={dataDesligamento}
            onChange={(e) => setDataDesligamento(e.target.value)}
          />
        </Form.Group>

        {carregandoFolhas ? (
          <div className="d-flex align-items-center gap-2 small text-muted">
            <Spinner animation="border" size="sm" />
            Verificando folhas de frequência…
          </div>
        ) : folhasPendentes.length > 0 ? (
          <Alert variant="warning" className="py-2 px-2 small mb-0">
            <div className="fw-semibold mb-1">
              Há {folhasPendentes.length} folha(s) não concluídas:
            </div>
            <ul className="mb-0 ps-3">
              {folhasPendentes.slice(0, 8).map((f) => (
                <li key={`${f.estagioId}-${f.ano}-${f.mes}`}>
                  {formatarMesAnoLegivel(f.ano, f.mes)} —{' '}
                  {attendanceSheetStatusEnumInFull(
                    f.status as Parameters<typeof attendanceSheetStatusEnumInFull>[0],
                  )}
                </li>
              ))}
              {folhasPendentes.length > 8 ? (
                <li>… e mais {folhasPendentes.length - 8}</li>
              ) : null}
            </ul>
          </Alert>
        ) : (
          <Alert variant="info" className="py-2 px-2 small mb-0">
            Nenhuma folha em status diferente de concluída foi encontrada.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button variant="secondary" size="sm" type="button" onClick={handleHide} disabled={confirmando}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          size="sm"
          type="button"
          onClick={handleConfirmar}
          disabled={confirmando || !dataDesligamento}
        >
          {confirmando ? 'Aplicando…' : 'Confirmar desligamento'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
